import { Alert, Box, Button, Collapse, Icon, IconButton } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useGetTenantQuery } from "~/gql/queries/tenant.generated";
import firstRunLinks from "~/helpers/firstRunLinks";
import { contactLinkAnchorProps } from "~/helpers/resolveContactLink";
import trialBannerDismissal from "~/helpers/trialBannerDismissal";
import trialBannerEscalation, { type TrialBannerBucket } from "~/helpers/trialBannerEscalation";

const BUCKET_ICONS: Record<TrialBannerBucket, string> = {
	critical: "error",
	normal: "schedule",
	warning: "warning"
};

const TrialBanner = () => {
	const { t } = useTranslation("common");
	const { data } = useGetTenantQuery();
	const [dismissedBuckets, setDismissedBuckets] = useState<ReadonlySet<TrialBannerBucket>>(
		trialBannerDismissal.readDismissedBuckets
	);

	const tenant = data?.tenant;
	const { bucket, labelCount, labelKey, severity } = trialBannerEscalation(tenant?.trialExpirationDate);
	const show = Boolean(tenant?.isTrial) && !dismissedBuckets.has(bucket);

	const dismiss = () => {
		trialBannerDismissal.markBucketDismissed(bucket);
		setDismissedBuckets(new Set(dismissedBuckets).add(bucket));
	};

	return (
		<Collapse in={show}>
			<Alert
				action={
					<Box sx={{ alignItems: "center", display: "flex", gap: 0.5 }}>
						{firstRunLinks.trialContactUrl && (
							<Button
								color="inherit"
								href={firstRunLinks.trialContactUrl}
								size="small"
								variant="outlined"
								{...contactLinkAnchorProps(firstRunLinks.trialContactUrl)}
							>
								{t("shell.trialBanner.talkToUs")}
							</Button>
						)}
						<IconButton aria-label={t("shell.trialBanner.dismissAria")} color="inherit" onClick={dismiss} size="small">
							<Icon fontSize="small">close</Icon>
						</IconButton>
					</Box>
				}
				icon={<Icon sx={{ fontSize: 20 }}>{BUCKET_ICONS[bucket]}</Icon>}
				severity={severity}
				sx={{ alignItems: "center", borderRadius: 0 }}
			>
				{t(labelKey, { count: labelCount })}
			</Alert>
		</Collapse>
	);
};

export default TrialBanner;
